/**
 * How often is this venue open? — derived from UitDatabank opening hours.
 *
 * Open-ended venue rows (zoos, playgrounds, museums, libraries, weekly markets)
 * carry `event_date` = the last sync date, which is meaningless as a badge: the
 * zoo is not "on" on the 6th, it is open every day. Rendering that date is the
 * gap left open by docs/PERPETUAL_VENUE_DECISIONS.md step 1, and this is what
 * replaces it.
 *
 * Pure and unit-tested on purpose (same reasoning as `transportCoverage.js` and
 * `dayOutHours.js`): the badge renders exactly what this returns, so the rules
 * are verifiable without mounting anything.
 *
 * The shape is SEMANTIC, not presentational — it returns which days, not a
 * string. Formatting (localized day names, separators) belongs to the badge,
 * which has `t` and the display locale.
 *
 * Two facts about the source data this encodes, both measured against prod:
 *
 *  - **`00:00–00:00` means "hours unknown", not "days unknown".** 1 in 5 rows
 *    carrying hours uses that sentinel for every block, but the `dayOfWeek`
 *    lists are still real — a Saturday antiques market and a Sat+Sun museum
 *    both look like this. Reading the days regardless of the times is what
 *    keeps ~250 prod rows on a real badge instead of the fallback.
 *  - **42% of permanent rows carry no `opening_hours` at all** (926 of 2,210).
 *    Those get `ongoing`, which claims only what `calendarType=permanent`
 *    already told us — a standing offering with no end — and never asserts
 *    daily availability we have not verified.
 */

// UiT spells days in lowercase English; map to JS getDay() indices so the badge
// can name them through Intl rather than seven translation keys per language.
const DAY_INDEX = {
  sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
  thursday: 4, friday: 5, saturday: 6
};

/**
 * Are these days one unbroken run round the week? Returns {from, to} or null.
 * Rotation-aware, so Fri–Mon (5,6,0,1) is a run, not a scattered list.
 */
function asRun(days) {
  const set = new Set(days);
  for (const start of days) {
    let unbroken = true;
    for (let i = 0; i < days.length; i++) {
      if (!set.has((start + i) % 7)) { unbroken = false; break; }
    }
    if (unbroken) return { from: start, to: (start + days.length - 1) % 7 };
  }
  return null;
}

/**
 * @param {object|null} customAttributes - parsed `custom_attributes` from the
 *   event row (normalizeCardData already turns the stored JSON into an object).
 * @returns {null
 *   | {kind:'daily'}
 *   | {kind:'weekly', day:number}
 *   | {kind:'closed', day:number}
 *   | {kind:'range', from:number, to:number}
 *   | {kind:'list', days:number[]}
 *   | {kind:'ongoing'}}
 *   null means "not an open-ended venue" — the caller should fall back to the
 *   ordinary date badge.
 */
export function deriveVenueFrequency(customAttributes) {
  // `is_permanent` is written by the sync for calendarType=permanent rows and
  // is the only marker that survives onto the card. It does NOT cover the ~18
  // periodic rows carrying UiT's own far-future sentinel; those keep the date
  // badge, which is what they did before this change too.
  if (!customAttributes || customAttributes.is_permanent !== true) return null;

  const blocks = customAttributes.opening_hours;
  if (!Array.isArray(blocks) || blocks.length === 0) return { kind: 'ongoing' };

  const days = new Set();
  for (const block of blocks) {
    const named = block && block.dayOfWeek;
    if (!Array.isArray(named)) continue;
    for (const name of named) {
      // hasOwnProperty, not a bare lookup: `constructor` and `__proto__` are
      // already lowercase, so they survive the normalize and would resolve to
      // an inherited value. A block naming `constructor` would otherwise make
      // days.size hit 7 and badge a closed-Mondays museum as "Open daily".
      // Same class of bug as the `constructor`-named category that reached
      // NaN in the ranking lexicons.
      const key = String(name).toLowerCase();
      if (!Object.prototype.hasOwnProperty.call(DAY_INDEX, key)) continue;
      days.add(DAY_INDEX[key]);
    }
  }

  if (days.size === 0) return { kind: 'ongoing' };
  if (days.size === 7) return { kind: 'daily' };

  // Six days open is the museum pattern (closed Mondays, closed Wednesdays).
  // Rendered as a run it wraps into "Thu-Tue", which asks the reader to work
  // out which day is missing; venues themselves always say "closed Wednesdays".
  // Naming the exception is both shorter and how the domain talks.
  if (days.size === 6) {
    for (let d = 0; d < 7; d++) if (!days.has(d)) return { kind: 'closed', day: d };
  }

  const sorted = [...days].sort((a, b) => a - b);
  if (sorted.length === 1) return { kind: 'weekly', day: sorted[0] };

  const run = asRun(sorted);
  return run ? { kind: 'range', ...run } : { kind: 'list', days: sorted };
}

export default deriveVenueFrequency;
